import { useState, useCallback } from "react";
import styles from "./frequencyselector.module.css";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKS = [
    { id: 1, label: "Week 1", range: "1st - 7th of month" },
    { id: 2, label: "Week 2", range: "8th - 14th of month" },
    { id: 3, label: "Week 3", range: "15th - 21st of month" },
    { id: 4, label: "Week 4", range: "22nd - 28th of month" },
];

const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
const CALENDAR_OFFSET = 0;

/* ---------------- DAILY ---------------- */
function DailyMode({ selectedDays, onChange }) {
    const allChecked = selectedDays.length === DAYS.length;

    const toggleAll = () => {
        onChange(allChecked ? [] : [...DAYS]);
    };

    const toggleDay = (day) => {
        onChange(selectedDays.includes(day) ? selectedDays.filter((d) => d !== day) : [...selectedDays, day]);
    };

    return (<div>
        <div className={`${styles.everydayChip} ${allChecked ? styles.active : ""}`} onClick={toggleAll}>
            <input type="checkbox" checked={allChecked} onChange={toggleAll} onClick={(e) => e.stopPropagation()} className={`${styles.checkbox}`} />
            Everyday
        </div>

        <div className={`${styles.dayChipsRow}`}>
            {DAYS.map((day) => {
                const checked = selectedDays.includes(day);
                return (<div key={day} className={`${styles.dayChip} ${checked ? styles.active : ""}`} onClick={() => toggleDay(day)}>
                    <input type="checkbox" checked={checked} onChange={() => toggleDay(day)} onClick={(e) => e.stopPropagation()} className={`${styles.checkbox}`} />
                    {day}
                </div>);
            })}
        </div>
    </div>);
}

/* ---------------- WEEKLY ---------------- */
function WeeklyMode({ selectedWeeks, onChange }) {
    const toggle = (id) => {
        onChange(
            selectedWeeks.includes(id)
                ? selectedWeeks.filter((w) => w !== id)
                : [...selectedWeeks, id]
        );
    };

    return (<div className={`${styles.WeeklyMode}`}>
        <p className={`${styles.weekHint}`}>Select which weeks of the month to repeat</p>

        <div className={`${styles.weekGrid}`}>
            {WEEKS.map((wk) => {
                const sel = selectedWeeks.includes(wk.id);
                return (<div key={wk.id} className={`${styles.weekCard} ${sel ? styles.active : ""}`} onClick={() => toggle(wk.id)}>
                    <div className={`${styles.weekCardTop}`}>
                        <span className={`${styles.weekCardLabel}`}>{wk.label}</span>

                        <div className={`${styles.weekCheck} ${sel ? styles.active : ""}`}>
                            {sel && <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" stroke-width="2"></polyline></svg>}
                        </div>
                    </div>

                    {/*} <div className={`${styles.weekCardRange}`}>{wk.range}</div>*/}
                </div>);
            })}
        </div>
    </div>);
}

/* ---------------- MONTHLY ---------------- */
function MonthlyMode({ selectedDates, onChange }) {
    const toggle = (d) => {
        onChange(selectedDates.includes(d) ? selectedDates.filter((x) => x !== d) : [...selectedDates, d]);
    };

    return (<div>
        <p className={`${styles.weekHint}`}>Select specific dates — repeats every month</p>

        <div className={`${styles.monthHeader}`}>
            <span className={`${styles.monthTitle}`}>Every Month</span>
            <button className={`${styles.clearBtn}`} onClick={() => onChange([])}>Clear all</button>
        </div>

        <div className={`${styles.weekdayRow}`}>
            {WEEKDAY_LABELS.map((l, i) => (<div key={i} className={`${styles.weekdayLabel}`}>{l}</div>))}
        </div>

        <div className={`${styles.calGrid}`}>
            {Array.from({ length: CALENDAR_OFFSET }).map((_, i) => (<div key={i} className={`${styles.calCellEmpty}`} />))}

            {MONTH_DAYS.map((d) => {
                const sel = selectedDates.includes(d);
                return (<div key={d} className={`${styles.calCell} ${sel ? styles.active : ""}`} onClick={() => toggle(d)}>{d}</div>);
            })}
        </div>
    </div>);
}

/* ---------------- SUMMARY ---------------- */
function Summary({ mode, selectedDays, selectedWeeks, selectedDates }) {
    const ordLabels = ["1st", "2nd", "3rd", "4th"];

    let repeatsOn = "";

    if (mode === "Daily" && selectedDays.length > 0) {
        repeatsOn = selectedDays.length === 7 ? "Everyday" : selectedDays.join(", ");
    }

    if (mode === "Weekly" && selectedWeeks.length > 0) {
        repeatsOn = [...selectedWeeks].sort().map((w) => ordLabels[w - 1]).join(" & ") + " week(s) of each month";
    }

    if (mode === "Monthly" && selectedDates.length > 0) {
        repeatsOn = [...selectedDates].sort((a, b) => a - b).map((d) => `${d}${ordinalSuffix(d)}`).join(", ") + " of each month";
    }

    if (!repeatsOn) return null;

    return (<div className={`${styles.summaryBox}`}>
        <div className={`${styles.summaryTitle}`}>Frequency Summary</div>

        <div className={`${styles.summaryRow}`}>
            <span className={`${styles.LabelSumm}`}>Type</span>
            <span className={`${styles.ValSumm}`}>{mode}</span>
        </div>

        <div className={`${styles.summaryRow}`}>
            <span className={`${styles.LabelSumm}`}>Repeats on</span>
            <span className={`${styles.ValSumm}`}>{repeatsOn}</span>
        </div>
    </div>);
}

function ordinalSuffix(n) {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
}

/* ---------------- MAIN ---------------- */
const FrequencySelector = ({ frequency, setFrequency, selectedDays, setSelectedDays, selectedWeeks, setSelectedWeeks, selectedDates, setSelectedDates }) => {
    const modes = ["Daily", "Weekly"];

    return (<div className={`${styles.wrapper}`}>
        <span className={`${styles.label}`}>Frequency</span>

        <div className={`${styles.tabBar}`}>
            {modes.map((m) => (<button type="button" key={m} className={`${styles.tab} ${frequency === m ? styles.activeTab : ""}`} onClick={() => { setFrequency(m); }}>{m}</button>))}
        </div>

        {frequency === "Daily" && (<DailyMode selectedDays={selectedDays} onChange={setSelectedDays} />)}

        {frequency === "Weekly" && (<>
            <WeeklyMode selectedWeeks={selectedWeeks} onChange={setSelectedWeeks} />
            <DailyMode selectedDays={selectedDays} onChange={setSelectedDays} />
        </>)}

        {/*frequency === "Monthly" && (<MonthlyMode selectedDates={selectedDates} onChange={setSelectedDates} />)*/}

        <Summary mode={frequency} selectedDays={selectedDays} selectedWeeks={selectedWeeks} selectedDates={selectedDates} />
    </div>);
}

export default FrequencySelector;